import {useEffect, useRef} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useServices} from '@/services/context'

const POLL_INTERVAL_MS = 2000

/**
 * Poll the effective access calculation status for an identity.
 * Only polls while `polling` is true (caller controls this, e.g. after a mutation).
 * When `lastCalculatedAt` changes, invalidates the provided query keys and calls `onComplete`.
 */
export function useCalculationStatus(
    identityId: string | undefined,
    invalidateKeys: string[][],
    polling: boolean,
    onComplete?: () => void,
) {
    const services = useServices()
    const queryClient = useQueryClient()
    const lastCalculatedRef = useRef<string | null>(null)

    const query = useQuery({
        queryKey: ['calculation-status', identityId],
        queryFn: () => services.analytics.calculationStatus(identityId!),
        enabled: !!identityId,
        refetchInterval: polling ? POLL_INTERVAL_MS : false,
    })

    useEffect(() => {
        if (!query.data?.lastCalculatedAt) return
        const current = query.data.lastCalculatedAt
        if (lastCalculatedRef.current && current !== lastCalculatedRef.current) {
            for (const key of invalidateKeys) {
                queryClient.invalidateQueries({queryKey: key})
            }
            onComplete?.()
        }
        lastCalculatedRef.current = current
    }, [query.data?.lastCalculatedAt, invalidateKeys, queryClient, onComplete])

    return {
        lastCalculatedAt: query.data?.lastCalculatedAt ?? null,
        lastError: query.data?.lastError ?? null,
        lastErrorAt: query.data?.lastErrorAt ?? null,
    }
}
